// Schedule shape stored on screen_revisions.schedule (jsonb).
//
// All times are interpreted in the player device's LOCAL time zone, never the
// server's. Values are persisted as strings/numbers so they survive a JSON
// round-trip without surprises.

export interface RevisionScheduleV1 {
  schemaVersion: 1;
  enabled: boolean;
  /**
   * `default` — the revision plays whenever no other scheduled revision wins
   *   AND the screen has it set as `active_revision_id`.
   * `windows` — the revision plays only inside one of `windows`.
   */
  mode: 'default' | 'windows';
  label?: string;
  priority?: number;
  windows?: ScheduleWindowV1[];
}

export interface ScheduleWindowV1 {
  id: string;
  label?: string;
  /** 0 = Sunday … 6 = Saturday, in the player's local time zone. */
  daysOfWeek: number[];
  /**
   * HH:mm, inclusive. Ignored when `allDay === true`.
   *
   * Optional in the type but always present when the window is not
   * all-day; a missing value is normalized to '00:00' and the window will
   * fail validation if it's also not an all-day window.
   */
  startTime: string;
  /**
   * HH:mm, exclusive. If startTime > endTime the window wraps midnight.
   * Ignored when `allDay === true`.
   */
  endTime: string;
  /**
   * If true, the window covers the entire local day on every selected
   * day-of-week (subject to start/end date fences). startTime/endTime are
   * ignored. This is the recommended way to express "Mon-Fri all day".
   */
  allDay?: boolean;
  /** YYYY-MM-DD, inclusive. Optional date floor. */
  startDate?: string;
  /** YYYY-MM-DD, inclusive. Optional date ceiling. */
  endDate?: string;
}

export const DEFAULT_SCHEDULE: RevisionScheduleV1 = {
  schemaVersion: 1,
  enabled: false,
  mode: 'default',
};

/**
 * Coerce an unknown jsonb value (e.g. legacy revision rows that never had the
 * column populated) into a well-formed RevisionScheduleV1.
 */
export function normalizeSchedule(input: unknown): RevisionScheduleV1 {
  if (!input || typeof input !== 'object') return { ...DEFAULT_SCHEDULE };
  const obj = input as Record<string, unknown>;
  const mode: RevisionScheduleV1['mode'] = obj.mode === 'windows' ? 'windows' : 'default';
  const windowsRaw = Array.isArray(obj.windows) ? (obj.windows as unknown[]) : [];
  const windows = windowsRaw
    .map((w) => normalizeWindow(w))
    .filter((w): w is ScheduleWindowV1 => !!w);
  return {
    schemaVersion: 1,
    enabled: !!obj.enabled,
    mode,
    label: typeof obj.label === 'string' ? obj.label : undefined,
    priority: typeof obj.priority === 'number' ? obj.priority : undefined,
    windows,
  };
}

function normalizeWindow(input: unknown): ScheduleWindowV1 | null {
  if (!input || typeof input !== 'object') return null;
  const obj = input as Record<string, unknown>;
  const allDay = obj.allDay === true;
  const startTimeRaw = typeof obj.startTime === 'string' ? obj.startTime : null;
  const endTimeRaw = typeof obj.endTime === 'string' ? obj.endTime : null;
  // For all-day windows, the times are ignored at match time but we still
  // carry them so toggling the checkbox in the UI doesn't lose the user's
  // previous values. Default to 00:00/00:00 if absent.
  const startTime = startTimeRaw && isHHmm(startTimeRaw) ? startTimeRaw : allDay ? '00:00' : null;
  const endTime = endTimeRaw && isHHmm(endTimeRaw) ? endTimeRaw : allDay ? '00:00' : null;
  if (!startTime || !endTime) return null;
  const daysRaw = Array.isArray(obj.daysOfWeek) ? (obj.daysOfWeek as unknown[]) : [];
  const daysOfWeek = daysRaw
    .map((d) => (typeof d === 'number' ? d : Number(d)))
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6);
  return {
    id: typeof obj.id === 'string' ? obj.id : crypto.randomUUID(),
    label: typeof obj.label === 'string' ? obj.label : undefined,
    daysOfWeek,
    startTime,
    endTime,
    allDay: allDay ? true : undefined,
    startDate: typeof obj.startDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(obj.startDate)
      ? obj.startDate
      : undefined,
    endDate: typeof obj.endDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(obj.endDate)
      ? obj.endDate
      : undefined,
  };
}

function isHHmm(s: string): boolean {
  return /^\d{2}:\d{2}$/.test(s) && Number(s.slice(0, 2)) < 24 && Number(s.slice(3, 5)) < 60;
}

// ---------------------------------------------------------------------------
// Pure schedule evaluation. Pure functions so unit/e2e tests can call them
// with synthetic clocks.
// ---------------------------------------------------------------------------

/**
 * True iff `now` (interpreted in the device's local time zone) falls inside
 * the given window. Handles cross-midnight windows (startTime > endTime)
 * and the `allDay` shortcut.
 */
export function windowMatches(window: ScheduleWindowV1, now: Date): boolean {
  if (window.daysOfWeek.length === 0) return false;

  const localYmd = formatLocalYmd(now);
  if (window.startDate && localYmd < window.startDate) return false;
  if (window.endDate && localYmd > window.endDate) return false;

  const todayDow = now.getDay();

  // All-day windows ignore start/end time and match the entire local day on
  // every selected day-of-week.
  if (window.allDay) {
    return window.daysOfWeek.includes(todayDow);
  }

  const hm = formatLocalHm(now);

  if (window.startTime <= window.endTime) {
    return window.daysOfWeek.includes(todayDow) && hm >= window.startTime && hm < window.endTime;
  }

  // Cross-midnight: window opens on one of daysOfWeek at startTime and runs
  // past midnight to endTime. Match either:
  //   - today is in daysOfWeek and we're past startTime, OR
  //   - yesterday is in daysOfWeek and we're before endTime.
  if (window.daysOfWeek.includes(todayDow) && hm >= window.startTime) return true;
  const yesterday = (todayDow + 6) % 7;
  if (window.daysOfWeek.includes(yesterday) && hm < window.endTime) return true;
  return false;
}

/** True iff any of the schedule's windows currently matches. */
export function scheduleMatches(schedule: RevisionScheduleV1, now: Date): boolean {
  if (!schedule.enabled) return false;
  if (schedule.mode !== 'windows') return false;
  if (!schedule.windows || schedule.windows.length === 0) return false;
  return schedule.windows.some((w) => windowMatches(w, now));
}

export interface ScheduleCandidate<T = unknown> {
  revisionId: string;
  schedule: RevisionScheduleV1;
  priority: number;
  /** Optional revision number — used to break priority ties deterministically. */
  revision?: number;
  /** Optional published-at timestamp (ms epoch or ISO string) — final tie-breaker. */
  publishedAt?: string | number;
  /** Caller-defined payload (e.g. the manifest itself). */
  value: T;
}

/**
 * Pick the best matching candidate. Tie-breaking is fully deterministic:
 *
 *   1. Highest `priority` wins.
 *   2. If tied, highest `revision` wins (newer publishes beat older
 *      republishes of the same daypart).
 *   3. If still tied, newest `publishedAt` wins.
 *   4. If still tied, lexicographically-greatest revisionId wins so two
 *      players with the same data always converge on the same choice.
 *
 * Returns null when no candidate matches `now`.
 */
export function pickScheduledCandidate<T>(
  candidates: ScheduleCandidate<T>[],
  now: Date,
): ScheduleCandidate<T> | null {
  let best: ScheduleCandidate<T> | null = null;
  for (const c of candidates) {
    if (!scheduleMatches(c.schedule, now)) continue;
    if (!best) {
      best = c;
      continue;
    }
    if (compareCandidates(c, best) > 0) best = c;
  }
  return best;
}

function compareCandidates(a: ScheduleCandidate, b: ScheduleCandidate): number {
  if (a.priority !== b.priority) return a.priority - b.priority;
  const aRev = typeof a.revision === 'number' ? a.revision : -Infinity;
  const bRev = typeof b.revision === 'number' ? b.revision : -Infinity;
  if (aRev !== bRev) return aRev - bRev;
  const aPub = toEpoch(a.publishedAt);
  const bPub = toEpoch(b.publishedAt);
  if (aPub !== bPub) return aPub - bPub;
  if (a.revisionId !== b.revisionId) return a.revisionId > b.revisionId ? 1 : -1;
  return 0;
}

function toEpoch(v: string | number | undefined): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : -Infinity;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : -Infinity;
  }
  return -Infinity;
}

function formatLocalHm(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function formatLocalYmd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// ---------------------------------------------------------------------------
// Bundle shape returned by the player heartbeat endpoint.
//
// The bundle is the canonical source of "what should this device play right
// now and in the near future". The server attaches freshly signed media URLs
// to every manifest in the bundle (see functions/api/player/heartbeat.ts).
// The bundle is short-lived w.r.t. its URLs but refreshed every sync tick.
// ---------------------------------------------------------------------------

import { normalizeBandwidth, type ScreenBandwidthSettingsV1 } from './screen-settings.ts';

export interface ScheduledRevisionEntry {
  revisionId: string;
  revision: number;
  manifest: unknown; // Manifest, but kept loose to avoid circular imports.
  schedule: RevisionScheduleV1;
  priority: number;
  /** Optional ISO timestamp; used as a deterministic tie-breaker. */
  publishedAt?: string;
}

export interface PlayerBundleV1 {
  schemaVersion: 1;
  defaultRevisionId: string | null;
  defaultManifest: unknown | null;
  scheduled: ScheduledRevisionEntry[];
  /**
   * Stable identity of the bundle's contents (revision IDs + schedules +
   * URL TTL). Players use this to detect when a sync produced a no-op so
   * they can skip re-caching. Never used for trust decisions.
   */
  bundleVersion?: string;
  /** Server timestamp (ms epoch) when the bundle was generated. */
  generatedAt?: number;
  /**
   * Server timestamp (ms epoch) at/after which the signed URLs in this
   * bundle should be assumed expired. The player aggressively refreshes
   * the bundle a couple of minutes before this time.
   */
  urlExpiresAt?: number;
  /**
   * Total number of schedule_enabled scheduled revisions for the screen
   * (BEFORE the server-side cap). Players surface this in diagnostics so
   * an admin can see when a screen has more dayparts than the bundle cap
   * allows for in a single sync.
   */
  scheduledCount?: number;
  /**
   * True when the server capped the `scheduled` array. The player keeps
   * polling and the bundle stays small; the truncated revisions are still
   * referenced by their lower-priority/older windows.
   */
  truncated?: boolean;
  /**
   * Optional bandwidth settings from the screen's settings JSONB. Carried in
   * the bundle so the player picks up changes (sync interval, quiet hours,
   * etc.) on its next heartbeat without an extra round-trip. Absent when
   * the screen has no bandwidth settings configured.
   */
  bandwidth?: ScreenBandwidthSettingsV1;
}

export function normalizeBundle(input: unknown): PlayerBundleV1 | null {
  if (!input || typeof input !== 'object') return null;
  const obj = input as Record<string, unknown>;
  const scheduledRaw = Array.isArray(obj.scheduled) ? (obj.scheduled as unknown[]) : [];
  const scheduled: ScheduledRevisionEntry[] = [];
  for (const s of scheduledRaw) {
    if (!s || typeof s !== 'object') continue;
    const o = s as Record<string, unknown>;
    if (typeof o.revisionId !== 'string') continue;
    if (!o.manifest) continue;
    scheduled.push({
      revisionId: o.revisionId,
      revision: typeof o.revision === 'number' ? o.revision : 0,
      manifest: o.manifest,
      schedule: normalizeSchedule(o.schedule),
      priority: typeof o.priority === 'number' ? o.priority : 0,
      publishedAt: typeof o.publishedAt === 'string' ? o.publishedAt : undefined,
    });
  }
  return {
    schemaVersion: 1,
    defaultRevisionId: typeof obj.defaultRevisionId === 'string' ? obj.defaultRevisionId : null,
    defaultManifest: obj.defaultManifest ?? null,
    scheduled,
    bundleVersion: typeof obj.bundleVersion === 'string' ? obj.bundleVersion : undefined,
    generatedAt: typeof obj.generatedAt === 'number' ? obj.generatedAt : undefined,
    urlExpiresAt: typeof obj.urlExpiresAt === 'number' ? obj.urlExpiresAt : undefined,
    scheduledCount:
      typeof obj.scheduledCount === 'number' ? obj.scheduledCount : undefined,
    truncated: typeof obj.truncated === 'boolean' ? obj.truncated : undefined,
    bandwidth: normalizeBandwidth(obj.bandwidth),
  };
}

// ---------------------------------------------------------------------------
// Schedule validation. Run client-side before persisting via
// updateRevisionSchedule, so users get inline error messages instead of a
// generic Postgres error.
// ---------------------------------------------------------------------------

export interface ScheduleValidationIssue {
  windowId?: string;
  field?: keyof ScheduleWindowV1 | 'mode' | 'enabled' | 'priority';
  message: string;
}

/**
 * Validate a schedule. Returns the list of issues; empty when valid.
 *
 * Rules enforced:
 *   - In `windows` mode there must be at least one window when `enabled`.
 *   - Each window must have at least one selected day-of-week.
 *   - startTime !== endTime (zero-length window would never match).
 *   - startDate <= endDate when both are set.
 *   - Priority must be a finite integer in a sane range.
 *   - HH:mm and YYYY-MM-DD must be syntactically valid.
 */
export function validateSchedule(schedule: RevisionScheduleV1): ScheduleValidationIssue[] {
  const issues: ScheduleValidationIssue[] = [];

  if (typeof schedule.priority === 'number') {
    if (!Number.isFinite(schedule.priority) || !Number.isInteger(schedule.priority)) {
      issues.push({ field: 'priority', message: 'Priority must be a whole number.' });
    } else if (schedule.priority < -1000 || schedule.priority > 1000) {
      issues.push({ field: 'priority', message: 'Priority must be between -1000 and 1000.' });
    }
  }

  if (schedule.mode !== 'windows') {
    return issues;
  }

  const windows = schedule.windows ?? [];
  if (schedule.enabled && windows.length === 0) {
    issues.push({
      field: 'enabled',
      message: 'Add at least one window before enabling this schedule.',
    });
  }

  const seenIds = new Set<string>();
  for (const w of windows) {
    if (seenIds.has(w.id)) {
      issues.push({ windowId: w.id, message: 'Duplicate window id.' });
    }
    seenIds.add(w.id);

    if (!Array.isArray(w.daysOfWeek) || w.daysOfWeek.length === 0) {
      issues.push({
        windowId: w.id,
        field: 'daysOfWeek',
        message: 'Pick at least one day for this window.',
      });
    } else {
      for (const d of w.daysOfWeek) {
        if (!Number.isInteger(d) || d < 0 || d > 6) {
          issues.push({
            windowId: w.id,
            field: 'daysOfWeek',
            message: `Invalid day-of-week: ${d}.`,
          });
        }
      }
    }

    // For all-day windows, time fields are ignored at match time. Skip the
    // strict HH:MM validation so users can leave the inputs blank.
    if (!w.allDay) {
      if (!isHHmmStrict(w.startTime)) {
        issues.push({
          windowId: w.id,
          field: 'startTime',
          message: 'Start time must be HH:MM (24-hour).',
        });
      }
      if (!isHHmmStrict(w.endTime)) {
        issues.push({
          windowId: w.id,
          field: 'endTime',
          message: 'End time must be HH:MM (24-hour).',
        });
      }
      if (
        isHHmmStrict(w.startTime) &&
        isHHmmStrict(w.endTime) &&
        w.startTime === w.endTime
      ) {
        issues.push({
          windowId: w.id,
          field: 'endTime',
          message: 'Start and end time must differ. Use the All-day toggle for a full day.',
        });
      }
    }

    if (w.startDate && !isYmdStrict(w.startDate)) {
      issues.push({
        windowId: w.id,
        field: 'startDate',
        message: 'Start date must be YYYY-MM-DD.',
      });
    }
    if (w.endDate && !isYmdStrict(w.endDate)) {
      issues.push({
        windowId: w.id,
        field: 'endDate',
        message: 'End date must be YYYY-MM-DD.',
      });
    }
    if (
      w.startDate &&
      w.endDate &&
      isYmdStrict(w.startDate) &&
      isYmdStrict(w.endDate) &&
      w.startDate > w.endDate
    ) {
      issues.push({
        windowId: w.id,
        field: 'endDate',
        message: 'End date must be on or after the start date.',
      });
    }
  }

  return issues;
}

function isHHmmStrict(s: string): boolean {
  if (!/^\d{2}:\d{2}$/.test(s)) return false;
  const h = Number(s.slice(0, 2));
  const m = Number(s.slice(3, 5));
  return h >= 0 && h < 24 && m >= 0 && m < 60;
}

function isYmdStrict(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime());
}

const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Render a window as a short human-readable string for the admin UI:
 *   "Mon–Fri, 09:00–17:00"
 *   "Sat–Sun, all day"
 *   "Mon, 22:00–02:00 (next day)"
 */
export function summarizeWindow(window: ScheduleWindowV1): string {
  const days = summarizeDays(window.daysOfWeek);
  if (window.allDay) return `${days}, all day`;
  const cross =
    window.startTime > window.endTime ? ' (next day)' : '';
  return `${days}, ${window.startTime}–${window.endTime}${cross}`;
}

function summarizeDays(daysOfWeek: number[]): string {
  if (daysOfWeek.length === 0) return 'No days';
  if (daysOfWeek.length === 7) return 'Every day';
  const sorted = [...new Set(daysOfWeek)].sort((a, b) => a - b);
  // Detect a single contiguous range.
  let contiguous = true;
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] !== sorted[i - 1] + 1) {
      contiguous = false;
      break;
    }
  }
  if (contiguous && sorted.length > 1) {
    return `${DAY_NAMES_SHORT[sorted[0]]}–${DAY_NAMES_SHORT[sorted[sorted.length - 1]]}`;
  }
  return sorted.map((d) => DAY_NAMES_SHORT[d]).join(', ');
}
